import { isMoment, Moment } from 'moment'

export function isRouteMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  flightItems: Array<App.Checkout.FlightItem>,
  flightViews: Array<App.Checkout.FlightItemView>,
): boolean {
  if (approvalRequest?.details?.flightItems && flightItems.length > 0 && flightViews.length > 0) {
    const departureRouteFromApprovalRequest = approvalRequest?.details?.flightItems.flatMap(item => item.flightGroup.flights.map(flight => `${flight.departureAirport}-${flight.arrivalAirport}`))
    const selectedDepartureRoutes = flightViews.flatMap(view => view.flights.flatMap(flight => flight.journeyFlight.flights.map(flight => `${flight.departingAirport}-${flight.arrivalAirport}`)))
    return departureRouteFromApprovalRequest.every((route, index) => selectedDepartureRoutes[index] === route)
  }

  return true
}

export function isAllDatesMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  flightItems: Array<App.Checkout.FlightItem>,
  flightViews: Array<App.Checkout.FlightItemView>,
): boolean {
  if (approvalRequest?.details?.flightItems && flightItems.length > 0 && flightViews.length > 0) {
    const departureDatesFromApprovalRequest = approvalRequest?.details?.flightItems.flatMap(item => item.flightGroup.flights.map(flight => flight.departingDate))
    const selectedDepartureDates = flightViews.flatMap(view => view.flights.flatMap(flight => flight.journeyFlight.flights.map(flight => flight.departingDate)))

    return departureDatesFromApprovalRequest.every((date, index) => selectedDepartureDates[index] === date)
  }

  return true
}

interface SearchRequestSegment {
  departureAirport?: {
    airportCode?: string
  }
  arrivalAirport?: {
    airportCode?: string
  }
  checkinDate?: Moment
}

export function isDateAndAirportsMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  segment?: SearchRequestSegment | undefined,
): boolean {
  if (segment?.checkinDate && approvalRequest?.details?.flightItems && isMoment(segment.checkinDate)) {
    const departureDatesFromApprovalRequest = approvalRequest?.details?.flightItems.flatMap(item =>
      item.flightGroup.flights.map(flight => flight.departingDate),
    )

    const dateMatches = departureDatesFromApprovalRequest.includes(segment.checkinDate.format('YYYY-MM-DD'))
    if (!dateMatches) {
      return false
    }

    // If no airport codes provided, just rely on the date match
    if (!segment.departureAirport?.airportCode || !segment.arrivalAirport?.airportCode) {
      return true
    }

    const airportPairsFromApprovalRequest = approvalRequest?.details?.flightItems.flatMap(item =>
      item.flightGroup.flights.map(flight => ({
        departureAirport: flight.departureAirport,
        arrivalAirport: flight.arrivalAirport,
      })),
    )

    // doesn't work with layovers because
    // it will look like this:
    // Object { departureAirport: "BNE", arrivalAirport: "MEL" }
    // Object { departureAirport: "MEL", arrivalAirport: "SYD" }
    // Object { departureAirport: "SYD", arrivalAirport: "MEL" }
    // Object { departureAirport: "MEL", arrivalAirport: "BNE" }
    // with the segment matching SYD to BNE
    return airportPairsFromApprovalRequest.some(
      pair =>
        pair.departureAirport === segment.departureAirport?.airportCode &&
        pair.arrivalAirport === segment.arrivalAirport?.airportCode,
    )
  }

  return false
}
